import React from 'react';
import { Skeleton } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { LenderTermDistributionRowViewModel } from '@trnsact/trnsact-shared-types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { LOOK_TO_BOOK_CHART_HEIGHT } from '../../../../constants';
import { formatPercentValue, getTermDistributionBuckets, mapTermDistributionDataset } from '../../../../lib';

interface Props {
  rows: LenderTermDistributionRowViewModel[];
  loading?: boolean;
}

const LABEL_CHAR_WIDTH = 7;
const LABEL_PADDING = 16;

export const TermDistributionByLenderChart = ({ rows, loading }: Props) => {
  const dataset = mapTermDistributionDataset(rows);
  const buckets = getTermDistributionBuckets(rows);

  const maxLenderNameLength = dataset.reduce((max, row) => Math.max(max, row.lender.length), 0);
  const leftMargin = maxLenderNameLength * LABEL_CHAR_WIDTH + LABEL_PADDING;

  return (
    <CardContainer title="Term Distribution by Lender" subTitle="% of funded deals at each term length">
      {loading ? (
        <Skeleton variant="rounded" height={LOOK_TO_BOOK_CHART_HEIGHT} />
      ) : (
        <BarChart
          height={LOOK_TO_BOOK_CHART_HEIGHT}
          layout="horizontal"
          dataset={dataset}
          margin={{ left: leftMargin, top: 70 }}
          barLabel={item => {
            const datum = dataset[item.dataIndex];
            const value = datum ? Number(datum[item.seriesId as string]) : 0;
            return value ? `${Math.round(value)}%` : '';
          }}
          yAxis={[{ scaleType: 'band', dataKey: 'lender' }]}
          xAxis={[{ min: 0, max: 100, tickMinStep: 20, valueFormatter: value => `${value}%` }]}
          series={buckets.map(bucket => ({
            id: String(bucket.term),
            dataKey: String(bucket.term),
            label: bucket.label,
            color: bucket.color,
            stack: 'total',
            valueFormatter: value => formatPercentValue(value as number),
          }))}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'left' },
              itemGap: 24,
              markGap: 8,
              itemMarkWidth: 16,
              itemMarkHeight: 16,
              padding: { bottom: 20 },
            },
            noDataOverlay: { message: 'No data available for the selected filters' },
          }}
        />
      )}
    </CardContainer>
  );
};
